<script setup>
import moment from "moment/moment";
import { computed, onMounted, ref } from "vue";
import forestreeHelpers from "@/helpers/forestreeHelpers";
import axios from "axios";
import AssignWork from "@/components/AssignWork.vue";

const props = defineProps(["permissions"]);

const page = ref(1);
const stumps = ref([]);
const stumpsRemoved = ref([]);
const users = ref(null);
const teams = ref([]);
const stumpsTotal = ref(null);
const contractors = ref(null);
const contractorId = ref(null);
const tasks = ref(null);
const costCentre = ref(null);
const poNumber = ref(null);
const notes = ref(null);
const showModal = ref(false);

const getPlantings = () => {
    axios
        .get("/api/tree/stumps")
        .then((response) => {
            stumps.value = response.data.trees;
            stumpsTotal.value = response.data.total;
            stumpsRemoved.value = response.data.removed;
            contractors.value = response.data.contractors;
            users.value = response.data.users;
            teams.value = response.data.teams;
        })
        .catch((error) => {
            console.log(error);
        });
};
const assignToContrator = () => {
    var stumpsToAssign = stumps.value.filter((s) => s.assigning === true);
    axios
        .post("/api/tree/stumps/assign", {
            stumps: stumpsToAssign,
            contractorId: this.contractorId,
            costCentre: this.costCentre,
            poNumber: this.poNumber,
            notes: this.notes,
        })
        .then((response) => {
            getPlantings();
            showModal.value = false;
            contractorId.value = null;
            costCentre.value = null;
            poNumber.value = null;
            notes.value = null;
        })
        .catch((error) => {
            console.log(error);
        });
};
const csvExport = () => {
    window.open("/api/tree/stumps/export");
};
const csvRecentExport = () => {
    window.open("/api/tree/stumps/recent/export");
};
onMounted(() => getPlantings());

const assigningReady = computed(() => {
    return stumps.value.filter((s) => s.assigning === true).length;
});
</script>

<template>
    <div class="flex flex-wrap lg:flex-no-wrap w-full lg:w-3/4 lg:p-4">
        <div class="h-auto bg-white forestree-box all-rounded w-full lg:ml-6">
            <div class="bg-white rounded shadow-sm px-1 lg:px-8 mt-6 pb-6">
                <div class="flex flex-wrap lg:flex-no-wrap my-6 py-4 w-full border-b border-slate-300">
                    <span
                        @click.prevent="page = 1"
                        :class="page === 1 ? 'text-blue-700 rounded-lg bg-slate-100' : 'text-slate-500'"
                        class="w-full text-center md:w-auto mr-0 md:mr-8 p-3 font-semibold cursor-pointer hover:text-blue-600"
                    >
                        Stumps to be Removed ({{ stumpsTotal }})
                    </span>
                    <span
                        @click.prevent="page = 2"
                        :class="page === 2 ? 'text-blue-700 rounded-lg bg-slate-100' : 'text-slate-500'"
                        class="w-full text-center md:w-auto mr-0 md:mr-8 p-3 font-semibold cursor-pointer hover:text-blue-600"
                    >
                        Recently Removed ({{ stumpsRemoved.length }})
                    </span>
                </div>

                <div v-if="page === 1" class="pb-6">
                    <div class="w-full pt-4 flex justify-between items-center">
                        <p class="my-3 text-xl text-slate-600 font-semibold">Stumps to be Removed ({{ stumps.length }})</p>

                        <div>
                            <button
                                @click.prevent="csvExport()"
                                class="bg-slate-100 hover:bg-slate-200 font-semibold hover:text-blue-500 text-blue-700 p-3 rounded-lg"
                            >
                                CSV Export
                            </button>
                        </div>
                    </div>

                    <div class="w-full">
                        <p class="my-3 text-md text-slate-500">
                            Assign Stumps to Users/Contractors using the Assigned To column. Click the Tree ID to view the location on the Stumps Map.
                        </p>
                    </div>
                </div>

                <div v-if="page === 2" class="pb-6">
                    <div class="w-full pt-4 flex justify-between items-center">
                        <p class="my-3 text-xl text-slate-600 font-semibold">Recently Removed Stumps ({{ stumpsRemoved.length }})</p>

                        <div>
                            <button
                                @click.prevent="csvRecentExport()"
                                class="bg-slate-100 hover:bg-slate-200 font-semibold hover:text-blue-500 text-blue-700 p-3 rounded-lg"
                            >
                                CSV Export
                            </button>
                        </div>
                    </div>

                    <div class="w-full">
                        <p class="my-3 text-md text-slate-500">A list of all the stumps removed in the last 3 months.</p>
                    </div>
                </div>

                <div v-if="page === 1" class="ftree-table striped hovered main-first-cell w-full overflow-x-auto py-4">
                    <div class="thead">
                        <div class="tr py-2">
                            <div class="td items-center w-1/2 lg:w-1/6">Tree ID</div>
                            <div class="td items-center w-1/2 lg:w-1/6">Stump Size</div>
                            <div class="td items-center w-1/2 lg:w-2/6">Approx Address & Project</div>
                            <div class="td items-center w-1/2 lg:w-1/6">Assigned To</div>
                            <div class="td items-center w-1/2 lg:w-1/6">Last Updated</div>
                        </div>
                    </div>
                    <div class="tbody text-left text-slate-700">
                        <div v-for="(tree, index) in stumps" :key="index" class="tr border-b border-slate-200">
                            <div class="td py-2 w-1/2 lg:w-1/6 items-center">
                                <!-- <input class="mr-2 leading-tight" type="checkbox" :id="'customCheck1'+tree.id" v-model="tree.assigning"> -->
                                <span
                                    v-if="tree.stump && tree.stump.priority === 1"
                                    class="w-5 mr-1 rounded-full ring-2 text-center ring-white bg-green-500 text-xs p-1 font-semibold text-white uppercase cursor-default"
                                    title="Low"
                                    >L</span
                                >
                                <span
                                    v-if="tree.stump && tree.stump.priority === 2"
                                    class="w-5 mr-1 rounded-full ring-2 text-center ring-white bg-orange-400 text-xs p-1 font-semibold text-white uppercase cursor-default"
                                    title="Medium"
                                    >M</span
                                >
                                <span
                                    v-if="tree.stump && tree.stump.priority === 3"
                                    class="w-5 mr-1 rounded-full ring-2 text-center ring-white bg-red-600 text-xs p-1 font-semibold text-white uppercase cursor-default"
                                    title="High"
                                    >H</span
                                >

                                <a :href="'/stumps?treeId=' + tree.id" class="py-2 text-slate-600 font-bold mr-3">#{{ tree.id }}</a>
                            </div>
                            <div class="td py-2 w-1/2 lg:w-1/6 items-center">
                                <span v-if="tree.stump_size" class="py-2 mr-2 font-bold">{{ tree.stump_size.name }}</span>
                            </div>
                            <div class="td py-2 w-1/2 lg:w-2/6 items-center">
                                <div class="w-full">
                                    <p v-if="tree.approx_address" class="py-2 mr-2 text-slate-500 text-xs">
                                        {{ tree.approx_address.length < 40 ? tree.approx_address : tree.approx_address.substring(0, 40) + ".." }}
                                    </p>
                                    <span v-else class="py-2 mr-2 text-slate-400 text-xs italic">No close address</span>
                                    <div v-if="tree.live_stump_projects && tree.live_stump_projects.length" class="py-0-5 mb-1">
                                        <a
                                            v-for="(p, idx) in tree.live_stump_projects"
                                            :key="'lv' + idx"
                                            :href="'/stumps?projectId=' + p.url_link"
                                            class="w-auto bg-red-700 rounded-full px-2 py-0-5 text-xs text-slate-200 mr-1"
                                        >
                                            {{ p.name }}
                                        </a>
                                    </div>
                                    <div v-if="tree.live_stump_projects && tree.stump_projects.length" class="py-0-5 mb-1">
                                        <a
                                            v-for="(p, idx) in tree.stump_projects"
                                            :key="'sv' + idx"
                                            :href="'/stumps?projectId=' + p.url_link"
                                            class="w-auto bg-blue-600 rounded-full px-2 py-0-5 text-xs text-slate-200 mr-1"
                                        >
                                            {{ p.name }}
                                        </a>
                                    </div>

                                    <div v-if="tree.defects" class="pb-2 pt-1">
                                        <span class="w-auto bg-slate-200 rounded-full px-2 py-0-5 text-xs text-slate-600 mr-1">
                                            Defect {{ tree.defects.defect_id }}
                                        </span>
                                        <span
                                            :class="tree.defects.defect_stage === 'Actioned' ? 'bg-blue-500' : 'bg-orange-500'"
                                            class="w-auto rounded-full px-2 py-0-5 text-xs text-slate-200 mr-1"
                                        >
                                            {{ tree.defects.defect_stage }}
                                        </span>
                                        <span
                                            :class="tree.defects.defect_status === 'Active' ? 'bg-green-500' : 'bg-red-500'"
                                            class="w-auto rounded-full px-2 py-0-5 text-xs text-slate-200 mr-1"
                                        >
                                            {{ tree.defects.defect_status }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div class="td py-2 w-1/2 lg:w-1/6 items-center">
                                <div class="whitespace-nowrap">
                                    <assign-work v-if="users" class="" :work="tree" :users="users" :teams="teams" assignTypeId="4" :permissions="permissions" />
                                </div>

                                <!-- <span 
                                  v-if="tree.contractor_assigned"
                                  class="w-auto bg-blue-600 rounded-full px-2 py-1 text-sm text-slate-200 mr-2">
                                    {{ tree.contractor_assigned.name }}
                                </span>
                                <span v-else class="py-2 mr-2 text-slate-400">unassigned</span> -->
                            </div>
                            <div class="td py-2 w-1/2 lg:w-1/6 items-center text-slate-500">{{ forestreeHelpers.dateTime(tree.updated_at) }}</div>
                        </div>
                    </div>
                </div>

                <div v-if="page === 2" class="ftree-table striped hovered main-first-cell w-full overflow-x-auto py-4">
                    <div class="thead">
                        <div class="tr py-2">
                            <div class="td items-center w-1/2 lg:w-1/6">Tree ID</div>
                            <div class="td items-center w-1/2 lg:w-1/6">Stump Size</div>
                            <div class="td items-center w-1/2 lg:w-2/6">Approx Address</div>
                            <div class="td items-center w-1/2 lg:w-1/6">Removed By</div>
                            <div class="td items-center w-1/2 lg:w-1/6">Removed</div>
                        </div>
                    </div>
                    <div class="tbody text-left text-slate-700">
                        <div v-for="(tree, index) in stumpsRemoved" :key="index" class="tr border-b border-slate-200 text-slate-500">
                            <div class="td py-2 w-1/2 lg:w-1/6 items-center">
                                <a :href="'/map?treeId=' + tree.id" class="py-2 text-slate-600 font-bold mr-3">#{{ tree.id }}</a>
                            </div>
                            <div class="td py-2 w-1/2 lg:w-1/6 items-center">
                                <span v-if="tree.stump_size" class="py-2 mr-2 font-bold">{{ tree.stump_size.name }}</span>
                                <span v-else class="py-2 mr-2 font-bold"></span>
                            </div>
                            <div class="td py-2 w-1/2 lg:w-2/6 items-center">
                                <span v-if="tree.approx_address" class="py-2 mr-2 text-slate-500 text-xs">{{
                                    tree.approx_address.length < 40 ? tree.approx_address : tree.approx_address.substring(0, 40) + ".."
                                }}</span>
                            </div>
                            <div class="td py-2 w-1/2 lg:w-1/6 items-center">{{ tree.name }}</div>
                            <div class="td py-2 w-1/2 lg:w-1/6 items-center text-slate-500">{{ forestreeHelpers.dateTime(tree.updated_at) }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <transition name="modal">
            <div class="modal-mask" v-if="showModal">
                <div class="modal-wrapper">
                    <div class="modal-container text-slate-700">
                        <div class="modal-header text-lg">
                            Assign to Contractor
                            <p class="text-sm text-slate-600">{{ assigningReady }} stumps selected</p>
                        </div>

                        <div class="modal-body text-sm">
                            <div class="flex flex-wrap -mx-3 mb-6">
                                <div class="w-full px-3 mb-6 md:mb-0">
                                    <label class="block uppercase tracking-wide text-slate-700 text-xs font-bold mb-2" for="grid-state"> Contractor </label>
                                    <div class="relative">
                                        <select
                                            v-model="contractorId"
                                            class="block appearance-none w-full bg-slate-100 border border-slate-200 text-slate-700 py-3 px-4 pr-8 rounded-sm leading-tight focus:outline-none focus:bg-white focus:border-slate-500"
                                        >
                                            <option v-for="contractor in contractors" :key="contractor.contractor_id" v-bind:value="contractor.contractor_id">
                                                {{ contractor.contractor.name }}
                                            </option>
                                        </select>
                                        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-700">
                                            <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="flex flex-wrap -mx-3 mb-6">
                                <div class="w-1/2 px-3 mb-6 md:mb-0">
                                    <label class="block uppercase tracking-wide text-slate-700 text-xs font-bold mb-2"> Cost Center </label>
                                    <input
                                        v-model="costCentre"
                                        class="appearance-none block w-full bg-slate-100 text-slate-700 border border-slate-200 rounded-sm py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-slate-500"
                                        type="text"
                                    />
                                </div>
                                <div class="w-1/2 px-3 mb-6 md:mb-0">
                                    <label class="block uppercase tracking-wide text-slate-700 text-xs font-bold mb-2"> PO </label>
                                    <input
                                        v-model="poNumber"
                                        class="appearance-none block w-full bg-slate-100 text-slate-700 border border-slate-200 rounded-sm py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-slate-500"
                                        type="text"
                                    />
                                </div>
                            </div>
                            <div class="flex flex-wrap -mx-3 mb-6">
                                <div class="w-full px-3 mb-6 md:mb-0">
                                    <label class="block uppercase tracking-wide text-slate-700 text-xs font-bold mb-2"> Notes </label>
                                    <textarea
                                        v-model="notes"
                                        class="appearance-none block w-full bg-slate-100 text-slate-700 border border-slate-200 rounded-sm py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-slate-500"
                                    >
                                    </textarea>
                                </div>
                            </div>
                            <p class="text-sm text-slate-500">NOTE: PO and Notes will be shown to the contractor.</p>
                        </div>

                        <div class="modal-footer">
                            <slot name="footer">
                                <button @click="showModal = false" class="bg-slate-100 hover:bg-slate-200 text-slate-600 py-2 px-4 rounded-full">Close</button>
                                <button v-if="!contractorId" class="bg-slate-100 text-white float-right py-2 px-4 rounded-full disable">Assign</button>
                                <button v-else @click="assignToContrator()" class="bg-blue-500 hover:bg-blue-700 text-white float-right py-2 px-4 rounded-full">
                                    Assign
                                </button>
                            </slot>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<style scoped>
.modal-mask {
    position: fixed;
    z-index: 9998;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: table;
    transition: opacity 0.3s ease;
}

.modal-wrapper {
    display: table-cell;
    vertical-align: middle;
}

.modal-container {
    width: 500px;
    margin: 0px auto;
    padding: 20px 30px;
    background-color: #fff;
    border-radius: 2px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
    transition: all 0.3s ease;
    font-family: Helvetica, Arial, sans-serif;
}

.modal-header h3 {
    margin-top: 0;
    color: #42b983;
}

.modal-body {
    margin: 20px 0;
}

.modal-default-button {
    float: right;
}

.modal-enter {
    opacity: 0;
}

.modal-leave-active {
    opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
    -webkit-transform: scale(1.1);
    transform: scale(1.1);
}

.py-0-5 {
    padding-top: 0.125rem;
    padding-bottom: 0.125rem;
}
</style>
